/**
 * JARVIS dock: the arc-reactor launcher (bottom-right) and a slide-in side
 * panel. Ctrl+J or Ctrl+Space toggles, Esc closes. Must render inside the
 * Router (uses useNavigate for the navigate tool). Hidden on /jarvis, where
 * the full-page view already shows the same conversation.
 *
 *   import JarvisDock from './components/jarvis/JarvisDock';
 *   <JarvisDock />            // once, inside <Router>, for signed-in non-producer users
 */
import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useJarvis, setOpen, toggleOpen, registerNavigator } from './jarvisStore';
import JarvisChat from './JarvisChat';
import ArcReactor from './ArcReactor';
import './jarvis.css';

export default function JarvisDock({ hideOnPaths = ['/jarvis'] }) {
  const s = useJarvis();
  const navigate = useNavigate();
  const location = useLocation();
  const launcherRef = useRef(null);
  const hidden = hideOnPaths.includes(location.pathname);

  useEffect(() => registerNavigator((path) => navigate(path)), [navigate]);

  useEffect(() => {
    const onKey = (e) => {
      const ctrl = e.ctrlKey || e.metaKey;
      if (ctrl && (e.key === 'j' || e.key === 'J' || e.code === 'Space')) {
        if (hidden) return;
        e.preventDefault();
        toggleOpen();
      } else if (e.key === 'Escape' && s.open) {
        setOpen(false);
        if (launcherRef.current) launcherRef.current.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hidden, s.open]);

  if (hidden) return null;
  const mode = s.listening ? 'listening' : s.speaking ? 'speaking' : s.streaming ? 'thinking' : s.status === 'offline' ? 'offline' : 'idle';

  return (
    <>
      {!s.open && (
        <button
          ref={launcherRef}
          type="button"
          className={`jv-launcher jv-launcher--${mode}`}
          onClick={() => setOpen(true)}
          aria-label="Open JARVIS (Ctrl+J)"
          title="JARVIS (Ctrl+J)"
        >
          <ArcReactor mode={mode} size="launcher" />
        </button>
      )}
      <div className={`jv-dock${s.open ? ' is-open' : ''}`} aria-hidden={!s.open}>
        {s.open && <JarvisChat variant="dock" onClose={() => setOpen(false)} autoFocus />}
      </div>
    </>
  );
}

export { useJarvis } from './jarvisStore';
